/**
 * NotificationBell — topbar inbox dropdown.
 *
 * Polls /api/v1/notifications/inbox every 60s for the current user, shows
 * the unread count badge on the bell, and renders the latest 50 items in
 * a scrollable popover. Each item has a severity dot (info/warning/error),
 * "Mark all read" CTA, and per-item delete.
 */
import { useEffect, useState, useCallback, useRef } from 'react';
import { Bell, X, AlertTriangle, Info, CheckCircle2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import api from '../../api/client';
import { useAuth } from '../../contexts/AuthContext';
import {
  Popover, PopoverContent, PopoverTrigger,
} from '../ui/popover';
import { Button } from '../ui/button';

const POLL_MS = 60_000;

const SEVERITY = {
  error:   { color: 'bg-rose-500', icon: AlertTriangle },
  warning: { color: 'bg-amber-500', icon: AlertTriangle },
  info:    { color: 'bg-sky-500', icon: Info },
  success: { color: 'bg-emerald-500', icon: CheckCircle2 },
};

function relativeTime(iso) {
  const t = new Date(iso).getTime();
  const diff = Date.now() - t;
  const m = Math.floor(diff / 60_000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

export default function NotificationBell() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const pollRef = useRef(null);

  const load = useCallback(async () => {
    if (!user) return;
    try {
      const res = await api.get('/notifications/inbox?limit=50');
      setItems(res.data.items || []);
      setUnread(res.data.unread_count || 0);
    } catch {
      // Silent — bell shouldn't yell on transient failures
    }
  }, [user]);

  useEffect(() => {
    load();
    pollRef.current = setInterval(load, POLL_MS);
    const onFocus = () => load();
    window.addEventListener('focus', onFocus);
    return () => {
      clearInterval(pollRef.current);
      window.removeEventListener('focus', onFocus);
    };
  }, [load]);

  const onOpen = (next) => {
    setOpen(next);
    if (next) load();
  };

  const markAllRead = async () => {
    if (unread === 0) return;
    setLoading(true);
    try {
      await api.post('/notifications/inbox/read-all');
      await load();
    } catch { toast.error('Failed to mark all read'); }
    finally { setLoading(false); }
  };

  const onClick = async (item) => {
    if (!item.read) {
      try {
        await api.post('/notifications/inbox/read', { inbox_ids: [item.inbox_id] });
      } catch { /* tolerated */ }
    }
    setOpen(false);
    await load();
    // Deep-link: prefer the server-provided link, else trainee profile, else trainees list.
    if (item.link && item.link.startsWith('/')) {
      navigate(item.link);
    } else if (item.trainee_id) {
      navigate('/trainees');
    } else {
      navigate('/trainees');
    }
  };

  const onDelete = async (e, item) => {
    e.stopPropagation();
    try {
      await api.delete(`/notifications/inbox/${item.inbox_id}`);
      await load();
    } catch { /* tolerated */ }
  };

  return (
    <Popover open={open} onOpenChange={onOpen}>
      <PopoverTrigger asChild>
        <button
          className="relative p-2 rounded-md hover:bg-muted"
          data-testid="notification-bell"
          aria-label={`Notifications${unread ? ` (${unread} unread)` : ''}`}
        >
          <Bell className="w-5 h-5 text-muted-foreground" />
          {unread > 0 && (
            <span
              className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-rose-500 text-white text-[10px] font-semibold flex items-center justify-center"
              data-testid="notification-bell-badge"
            >
              {unread > 99 ? '99+' : unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-96 p-0" data-testid="notification-popover">
        <div className="flex items-center justify-between p-3 border-b">
          <div>
            <p className="font-display text-sm font-semibold">Notifications</p>
            <p className="text-xs text-muted-foreground">
              {unread > 0 ? `${unread} unread` : 'All caught up'}
            </p>
          </div>
          {unread > 0 && (
            <Button size="sm" variant="ghost" onClick={markAllRead}
                    disabled={loading} data-testid="mark-all-read-btn">
              Mark all read
            </Button>
          )}
        </div>
        <div className="max-h-96 overflow-y-auto" data-testid="notification-list">
          {items.length === 0 ? (
            <div className="p-8 text-center text-sm text-muted-foreground">
              <Bell className="w-8 h-8 mx-auto mb-2 opacity-40" />
              Nothing to see yet.
            </div>
          ) : (
            <div className="divide-y">
              {items.map(item => {
                const sev = SEVERITY[item.severity] || SEVERITY.info;
                const Icon = sev.icon;
                return (
                  <button
                    key={item.inbox_id}
                    onClick={() => onClick(item)}
                    className={`group w-full text-left p-3 hover:bg-muted/40 transition flex items-start gap-3 ${
                      !item.read ? 'bg-sky-50/40' : ''
                    }`}
                    data-testid={`notification-item-${item.inbox_id}`}
                  >
                    <span className={`shrink-0 mt-0.5 w-7 h-7 rounded-full flex items-center justify-center ${sev.color}/15`}>
                      <Icon className={`w-4 h-4 text-white p-0.5 rounded-full ${sev.color}`} />
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <p className={`text-sm truncate ${!item.read ? 'font-semibold' : ''}`}>
                          {item.title}
                        </p>
                        <button
                          onClick={(e) => onDelete(e, item)}
                          className="opacity-0 group-hover:opacity-100 p-0.5 rounded hover:bg-muted shrink-0"
                          aria-label="Dismiss"
                        >
                          <X className="w-3.5 h-3.5 text-muted-foreground" />
                        </button>
                      </div>
                      <p className="text-xs text-muted-foreground line-clamp-2 mt-0.5">
                        {item.message}
                      </p>
                      <p className="text-[10px] text-muted-foreground mt-1">
                        {relativeTime(item.created_at)}
                      </p>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
